import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeftRight,
  Plus,
  ArrowRight,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Truck,
  Package,
  MoreVertical,
  FileText,
} from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { itemTransfersApi, type ItemTransfer, type TransferStatus } from "@/lib/api/item-transfers";
import { cn } from "@/lib/utils";

const statusConfig: Record<
  TransferStatus,
  { label: string; cls: string; icon: React.ElementType }
> = {
  PENDING: { label: "Pending", cls: "bg-yellow-100 text-yellow-700", icon: Clock },
  IN_TRANSIT: { label: "In Transit", cls: "bg-primary/10 text-primary", icon: Truck },
  COMPLETED: { label: "Completed", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-100 text-gray-500", icon: XCircle },
};

type TabFilter = "all" | "outbound" | "inbound";

const BranchTransfersPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: allBranchesRaw = [] } = useActiveBranches();
  const allBranches = allBranchesRaw.filter((b) => b.id !== id);

  const [branch, setBranch] = useState<Branch | null>(null);
  const [transfers, setTransfers] = useState<ItemTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabFilter>("all");

  // Request transfer modal
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestData, setRequestData] = useState({ itemBarcode: "", toBranchId: "", notes: "" });
  const [requestLoading, setRequestLoading] = useState(false);

  // Action modal
  const [actionTransfer, setActionTransfer] = useState<ItemTransfer | null>(null);
  const [actionType, setActionType] = useState<"approve" | "complete" | "cancel" | null>(null);
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // View note modal
  const [noteTransfer, setNoteTransfer] = useState<ItemTransfer | null>(null);

  useEffect(() => {
    if (id) loadData();
  }, [id, tab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [branchData, transferData] = await Promise.all([
        branchesApi.getById(id!),
        itemTransfersApi.getAll({
          branchId: id,
          direction: tab === "all" ? undefined : tab,
        }),
      ]);
      setBranch(branchData);
      setTransfers(transferData);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load transfers.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  // ── Request transfer (by barcode) ──
  const handleRequestByBarcode = async (barcode: string, toBranchId: string, notes: string) => {
    try {
      setRequestLoading(true);
      await itemTransfersApi.create({
        itemBarcode: barcode.trim(),
        fromBranchId: id!,
        toBranchId,
        notes: notes || undefined,
      });
      toast.success("Transfer request submitted.");
      setRequestOpen(false);
      setRequestData({ itemBarcode: "", toBranchId: "", notes: "" });
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create transfer.");
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Approve / Complete / Cancel ──
  const openAction = (transfer: ItemTransfer, type: "approve" | "complete" | "cancel") => {
    setActionTransfer(transfer);
    setActionType(type);
    setActionNotes("");
  };

  const handleAction = async () => {
    if (!actionTransfer || !actionType) return;
    try {
      setActionLoading(true);
      if (actionType === "approve") await itemTransfersApi.approve(actionTransfer.id, actionNotes);
      else if (actionType === "complete")
        await itemTransfersApi.complete(actionTransfer.id, actionNotes);
      else if (actionType === "cancel")
        await itemTransfersApi.cancel(actionTransfer.id, actionNotes);
      toast.success(
        `Transfer ${actionType === "approve" ? "approved & dispatched" : actionType === "complete" ? "completed" : "cancelled"}.`
      );
      setActionTransfer(null);
      setActionType(null);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredTransfers = transfers;

  const pendingOut = transfers.filter(
    (t) => t.status === "PENDING" && t.fromBranchId === id
  ).length;
  const inTransitIn = transfers.filter(
    (t) => t.status === "IN_TRANSIT" && t.toBranchId === id
  ).length;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="wide">
        <PageHeader
          title="Item Transfers"
          description={`Inbound and outbound item movements for ${branch?.name ?? "this branch"}`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch?.name ?? "Branch", href: id ? `/setup/branches/${id}` : undefined },
            { label: "Transfers" },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          actions={
            <Button onClick={() => setRequestOpen(true)} className="gap-2">
              <ArrowRight className="w-4 h-4" />
              Send Item Out of Branch
            </Button>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Pending outbound",
              value: pendingOut,
              icon: ArrowRight,
              cls: "text-yellow-600 bg-yellow-50",
            },
            {
              label: "Awaiting receipt",
              value: inTransitIn,
              icon: Package,
              cls: "text-primary bg-primary/5",
            },
            {
              label: "Total transfers",
              value: transfers.length,
              icon: ArrowLeftRight,
              cls: "text-gray-600 bg-gray-50",
            },
            {
              label: "Completed",
              value: transfers.filter((t) => t.status === "COMPLETED").length,
              icon: CheckCircle2,
              cls: "text-green-600 bg-green-50",
            },
          ].map((s) => (
            <Card key={s.label} className="p-4 flex items-center gap-3">
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                  s.cls
                )}
              >
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* Tab filter */}
        <div className="flex gap-1 border-b border-gray-200">
          {(["all", "outbound", "inbound"] as TabFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize",
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              )}
            >
              {t === "all" ? "All" : t === "outbound" ? "↑ Outbound" : "↓ Inbound"}
            </button>
          ))}
        </div>

        {/* Transfers table */}
        {filteredTransfers.length === 0 ? (
          <Card className="p-12 text-center">
            <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No transfers found</p>
            <p className="text-sm text-gray-400 mt-1">
              {tab === "all"
                ? "No item transfers have been requested for this branch yet."
                : `No ${tab} transfers found.`}
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Item</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden sm:table-cell">
                    Route
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 hidden md:table-cell">
                    Requested
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTransfers.map((t) => {
                  const cfg = statusConfig[t.status];
                  const StatusIcon = cfg.icon;
                  const isOutbound = t.fromBranchId === id;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 truncate max-w-xs">
                          {t.item?.biblio?.title ?? "Unknown item"}
                        </p>
                        <p className="text-xs text-gray-500">
                          Barcode: {t.item?.barcode ?? "—"} · Acc: {t.item?.accessionNumber ?? "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <span
                            className={cn(
                              "font-medium",
                              isOutbound ? "text-amber-600" : "text-primary"
                            )}
                          >
                            {t.fromBranch?.name}
                          </span>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span
                            className={cn(
                              "font-medium",
                              !isOutbound ? "text-amber-600" : "text-primary"
                            )}
                          >
                            {t.toBranch?.name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {isOutbound ? "↑ Outbound" : "↓ Inbound"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
                            cfg.cls
                          )}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                        {new Date(t.requestedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end items-center">
                          {/* Outbound PENDING: sender can approve/dispatch */}
                          {t.status === "PENDING" && isOutbound && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs gap-1"
                                onClick={() => openAction(t, "approve")}
                              >
                                <Truck className="w-3 h-3" /> Dispatch
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-xs text-red-500 hover:text-red-700"
                                onClick={() => openAction(t, "cancel")}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {/* Inbound IN_TRANSIT: receiver marks as received */}
                          {t.status === "IN_TRANSIT" && !isOutbound && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50"
                              onClick={() => openAction(t, "complete")}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Received
                            </Button>
                          )}
                          {/* Pending can also be cancelled by the destination branch */}
                          {t.status === "PENDING" && !isOutbound && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs text-red-500 hover:text-red-700"
                              onClick={() => openAction(t, "cancel")}
                            >
                              Decline
                            </Button>
                          )}
                          {t.notes && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs gap-1 text-gray-500 hover:text-gray-700"
                              onClick={() => setNoteTransfer(t)}
                              title="View Note"
                            >
                              <FileText className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </PageContainer>

      {/* ── Request Transfer Modal ── */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-primary" />
              Request Item Transfer
            </DialogTitle>
            <DialogDescription>
              Send an item from <strong>{branch?.name}</strong> to another branch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="itemBarcode">Item Barcode</Label>
              <p className="text-xs text-gray-500 mb-1.5">
                Enter the barcode from the item&apos;s spine label, or from the{" "}
                <button
                  type="button"
                  onClick={() => {
                    setRequestOpen(false);
                    navigate("/catalog");
                  }}
                  className="underline hover:text-gray-700 text-primary"
                >
                  Catalog
                </button>{" "}
                (open a book → Item copies table shows barcode). Prefer adding items via Catalog →
                &quot;Add to Transfer Queue&quot; on each copy, then use the Transfer Queue button
                in the header.
              </p>
              <Input
                id="itemBarcode"
                placeholder="e.g. BARCODE-001 or scan"
                value={requestData.itemBarcode}
                onChange={(e) => setRequestData((p) => ({ ...p, itemBarcode: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="toBranch">Destination Branch</Label>
              <select
                id="toBranch"
                value={requestData.toBranchId}
                onChange={(e) => setRequestData((p) => ({ ...p, toBranchId: e.target.value }))}
                className="w-full mt-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select a branch…</option>
                {allBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                placeholder="Reason for transfer…"
                value={requestData.notes}
                onChange={(e) => setRequestData((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setRequestOpen(false)}
                disabled={requestLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  handleRequestByBarcode(
                    requestData.itemBarcode,
                    requestData.toBranchId,
                    requestData.notes
                  )
                }
                disabled={!requestData.itemBarcode || !requestData.toBranchId || requestLoading}
                className="gap-2"
              >
                {requestLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── View Note Modal ── */}
      <Dialog
        open={!!noteTransfer}
        onOpenChange={(v) => {
          if (!v) setNoteTransfer(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Transfer Note
            </DialogTitle>
            <DialogDescription>
              Note for "{noteTransfer?.item?.biblio?.title ?? "Unknown item"}" (
              {noteTransfer?.fromBranch?.name} → {noteTransfer?.toBranch?.name})
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
            {noteTransfer?.notes || "No note available."}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setNoteTransfer(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Action Confirmation Modal ── */}
      <Dialog
        open={!!actionTransfer}
        onOpenChange={(v) => {
          if (!v) {
            setActionTransfer(null);
            setActionType(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve"
                ? "Dispatch Item"
                : actionType === "complete"
                  ? "Confirm Receipt"
                  : "Cancel Transfer"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve" &&
                `Mark "${actionTransfer?.item?.biblio?.title}" as dispatched to ${actionTransfer?.toBranch?.name}.`}
              {actionType === "complete" &&
                `Confirm receipt of "${actionTransfer?.item?.biblio?.title}" from ${actionTransfer?.fromBranch?.name}.`}
              {actionType === "cancel" &&
                `Cancel the transfer of "${actionTransfer?.item?.biblio?.title}". The item will remain at ${actionTransfer?.fromBranch?.name}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Add a note…"
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setActionTransfer(null);
                  setActionType(null);
                }}
                disabled={actionLoading}
              >
                Back
              </Button>
              <Button
                onClick={handleAction}
                disabled={actionLoading}
                className={cn("gap-2", actionType === "cancel" && "bg-red-600 hover:bg-red-700")}
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {actionType === "approve"
                  ? "Dispatch"
                  : actionType === "complete"
                    ? "Confirm Receipt"
                    : "Cancel Transfer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default BranchTransfersPage;
